import { useCallback, useState } from "react";
import { toast } from "react-toastify";

import { useMutation, useQuery } from "@apollo/client";
import { TYPE_WELLS } from "constants/settings.constants";

import { FOLDERS, SAVE_FOLDER_WITH_TYPE_WELLS } from "api/userArps";

import { ForecastFolder } from "models/UserArpsModel";

interface UseFolderSaveAsParams {
  selectedProject?: { projectId: string };
  selectedFolder: ForecastFolder;
  saveAsToProjectId: string;
  saveAsTitle: string;
  onSuccess: () => void;
  onError: (message: string) => void;
}

export function useFolderSaveAs({
  selectedProject,
  selectedFolder,
  saveAsToProjectId,
  saveAsTitle,
  onSuccess,
  onError
}: UseFolderSaveAsParams) {
  const [isCloning, setIsCloning] = useState(false);

  const [saveFolderWithForecasts] = useMutation(SAVE_FOLDER_WITH_TYPE_WELLS);

  const { data: currentProjectFolders } = useQuery(FOLDERS, {
    variables: {
      req: {
        projectId: selectedProject?.projectId,
        type: TYPE_WELLS
      }
    },
    skip: !selectedProject?.projectId
  });

  const { data: targetProjectFolders } = useQuery(FOLDERS, {
    variables: {
      req: {
        projectId: saveAsToProjectId,
        type: TYPE_WELLS
      }
    },
    skip: !saveAsToProjectId || saveAsToProjectId === selectedProject?.projectId
  });

  const copyFolder = useCallback(
    async (projectId: string, folderName: string, folderToCopy: ForecastFolder) => {
      const input = {
        sourceFolderId: folderToCopy.folderId,
        targetProjectId: projectId,
        newName: folderName,
        type: TYPE_WELLS
      };

      await saveFolderWithForecasts({
        variables: { input },
        refetchQueries: [
          {
            query: FOLDERS,
            variables: {
              req: {
                projectId: projectId,
                type: TYPE_WELLS
              }
            }
          }
        ],
        awaitRefetchQueries: true
      });
    },
    [saveFolderWithForecasts]
  );

  const handleSave = useCallback(async () => {
    const trimmedTitle = saveAsTitle.trim();

    if (!trimmedTitle) {
      onError("Folder name cannot be empty");
      return;
    }

    // Determine which folder list to check based on target project
    const isSavingToCurrentProject = saveAsToProjectId === selectedProject?.projectId;
    const foldersToCheck = isSavingToCurrentProject
      ? currentProjectFolders?.folders || []
      : targetProjectFolders?.folders || [];

    // Check for duplicate folder name
    const folderExists = foldersToCheck.some(
      (folder) => folder.name.toLowerCase() === trimmedTitle.toLowerCase()
    );

    if (folderExists) {
      onError("Folder name already exists in selected project");
      return;
    }

    // Clear any previous errors
    onError("");
    setIsCloning(true);

    try {
      await copyFolder(saveAsToProjectId, trimmedTitle, selectedFolder);

      toast.success(`Folder "${trimmedTitle}" cloned successfully`);
      onSuccess();
    } catch (error) {
      toast.error("Failed to clone folder. Please try again.");
    } finally {
      setIsCloning(false);
    }
  }, [
    saveAsTitle,
    saveAsToProjectId,
    selectedFolder,
    currentProjectFolders,
    targetProjectFolders,
    selectedProject?.projectId,
    copyFolder,
    onSuccess,
    onError
  ]);

  return {
    handleSave,
    isCloning
  };
}
